import { useState } from "react";
import { getIn } from "formik";
import * as yup from "yup";
import {
  Box, CircularProgress,
  Container,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import ReactQuill from "react-quill";
import Dialog from "components/dialog/dialog";
import ModalConfirm from "components/modalConfirm/modalConfirm";
import FORMIK from "modules/formik";
import type { WhatsGoingOnDetailProps } from "modules/types";
import GLOBAL from "modules/global";

const MAX_FILE_SIZE_IN_KB = 500;

// Validation schema
const validationSchema = yup.object<typeof initialWgo>({
  name: yup.string().required("Name is required"),
  sequenceData: yup.number().nullable(),
  logoImage: yup.mixed().test("fileSize", `File too large (max ${MAX_FILE_SIZE_IN_KB}kb)`, (value) => {
    if (value instanceof File) return value.size <= MAX_FILE_SIZE_IN_KB * 1024;
    return true;
  }).required("Logo image is required"),
  campaignMedia: yup.mixed().nullable().test("fileSize", `File too large (max ${MAX_FILE_SIZE_IN_KB}kb)`, (value) => {
    if (value instanceof File) return value.size <= MAX_FILE_SIZE_IN_KB * 1024;
    return true;
  }),
  isButton: yup.boolean(),
  urlButton: yup.string().when("isButton", {
    is: true,
    then: s => s.required("Button URL is required"),
    otherwise: s => s.nullable(),
  }),
  isActive: yup.boolean(),
  campaignContent: yup.array().of(
    yup.object({
      language: yup.string().required("Language is required"),
      title: yup.string().required("Title is required"),
      labelDate: yup.string().nullable(),
      buttonName: yup.string().test(
        'has-name',
        'Button Name is required',
        (val, ctx) => {
          const isButton = ctx.from?.[1].value.isButton
          return isButton ? !!val : true
        }
      ),
      informationBody: yup.string().nullable(),
      tncBody: yup.string().nullable(),
    })
  ),
});

export const initialWgo: WhatsGoingOnDetailProps = {
  name: "",
  sequenceData: 0,
  logoImage: "",
  campaignMedia: "",
  isButton: false,
  urlButton: "",
  isActive: false,
  campaignContent: [
    {
      language: "ID",
      title: "",
      labelDate: "",
      buttonName: "",
      informationBody: "",
      tncBody: "",
    },
    {
      language: "en-US",
      title: "",
      labelDate: "",
      buttonName: "",
      informationBody: "",
      tncBody: "",
    }
  ],
};


export default function WhatsGoingOnForm({
  initialValues = initialWgo,
  onSubmit,
  open,
  onClose,
  loading,
  fetching,
}: {
    initialValues?: Partial<typeof initialWgo>;
  onSubmit: (formData: FormData) => void;
  open: boolean;
  onClose: () => void;
  loading: boolean;
  fetching?: boolean;
}) {
  const [isConfirm, setIsConfirm] = useState(false);

  function handleSubmit({ logoImage, campaignMedia, ...values }: typeof initialValues) {
    const formData = new FormData();
    formData.set("logoFile", logoImage as string);
    if (campaignMedia) {
      formData.set("mediaFile", campaignMedia as string);
    }
    formData.set("campaignData", JSON.stringify(values));
    onSubmit(formData);
  }

  const formik = FORMIK.useFormFormik<typeof initialValues>(
    validationSchema,
    initialValues,
    handleSubmit
  );

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setTouched, validateForm, submitForm } = formik;

  function getHelperText(field: string) {
    return getIn(touched, field) && getIn(errors, field);
  }

  return (
    <Dialog
      title={fetching ? "Loading..." : values.id ? "Edit Campaign" : "Create Campaign"}
      open={open}
      onCancel={onClose}
      onSubmit={() => setIsConfirm(true)}
      loading={loading}
      isDisabled={!formik.isValid}
      maxWidth="xl"
    >
      <ModalConfirm
        open={isConfirm}
        onClose={() => setIsConfirm(false)}
        text="Save"
        text2="Confirm campaign submission?"
        onSubmit={() => {
          validateForm(values).then((res) => {
            if (Object.keys(res).length === 0) {
              setIsConfirm(false);
              submitForm();
            } else {
              setIsConfirm(false);
            }
          });
        }}
        loading={loading}
        title="Save Campaign"
      />

      <Container sx={{ display: "flex", gap: 2 }}>
        {fetching && <CircularProgress sx={{ marginX: "auto" }} />}
        {!fetching && (
          <>
            {/* Basic Info */}
            <Box sx={{ border: "1px solid #ccc", padding: 2, display: 'flex', flexDirection: "column", gap: 2, flex: 1 }}>
              <Typography variant="h6">General</Typography>
              <TextField
                label="Campaign Name"
                name="name"
                value={values.name || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={getHelperText('name')}
                fullWidth
              />
              <TextField
                label="Sequence Data"
                name="sequenceData"
                type="number"
                value={values.sequenceData || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />

              {/* Logo Upload */}
              <TextField
                label="Logo Image"
                type="file"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/png,image/jpeg" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTouched({ ...touched, logoImage: true });
                  setFieldValue("logoImage", e.target.files?.[0]);
                }}
                helperText={getHelperText('logoImage')}
                error={Boolean(touched.logoImage && errors.logoImage)}
              />
              {values.logoImage && (
                <Box>
                  <Typography>Logo Preview:</Typography>
                  <img
                    src={typeof values.logoImage === "object" ? URL.createObjectURL(values.logoImage) : values.logoImage}
                    alt="Logo"
                    style={{ maxWidth: "200px" }}
                  />
                </Box>
              )}

              {/* Campaign Media Upload */}
              <TextField
                label="Campaign Media"
                type="file"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/png,image/jpeg" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTouched({ ...touched, campaignMedia: true });
                  setFieldValue("campaignMedia", e.target.files?.[0]);
                }}
                helperText={getHelperText('campaignMedia')}
                error={Boolean(touched.campaignMedia && errors.campaignMedia)}
              />
              {values.campaignMedia && (
                <Box>
                  <Typography>Media Preview:</Typography>
                  <img
                    src={typeof values.campaignMedia === "object" ? URL.createObjectURL(values.campaignMedia) : values.campaignMedia}
                    alt="Media"
                    style={{ maxWidth: "200px" }}
                  />
                </Box>
              )}

              {/* Boolean fields */}
              <FormControlLabel
                control={<Checkbox checked={values.isButton || false} onChange={(e) => setFieldValue("isButton", e.target.checked, true)} />}
                label="Has Button"
              />
              <TextField
                label="Button URL"
                name="urlButton"
                value={values.urlButton || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={getHelperText('urlButton')}
                error={Boolean(touched.urlButton && errors.urlButton)}
                fullWidth
              />
              <FormControlLabel
                control={<Checkbox checked={values.isActive || false} onChange={(e) => setFieldValue("isActive", e.target.checked)} />}
                label="Active"
              />
            </Box>

            <Box sx={{ border: "1px solid #ccc", padding: 2, flex: 2 }}>
              <Typography variant="h6">Campaign Contents</Typography>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: 'start', gap: 2, }}>
                {/* Campaign Contents */}
                {values?.campaignContent?.map((content, index: number) => (
                  <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                    <Typography>Language: {content.language}</Typography>
                    <TextField
                      label="Title"
                      name={`campaignContent[${index}].title`}
                      value={content.title || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={getIn(touched, `campaignContent[${index}].title`) && getIn(errors, `campaignContent[${index}].title`)}
                      helperText={getHelperText(`campaignContent[${index}].title`)}
                    />

                    <ReactQuill
                      theme='snow'
                      value={content.informationBody || ""}

                      onChange={(e) => formik.setFieldValue(
                        `campaignContent[${index}].informationBody`,
                        GLOBAL.emptyQuill(e)
                      )}
                      style={{ minHeight: 200 }}
                    />

                    <TextField
                      label="Label Date"
                      name={`campaignContent[${index}].labelDate`}
                      value={content.labelDate || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />

                    <TextField
                      label="Button Label"
                      name={`campaignContent[${index}].buttonName`}
                      value={content.buttonName || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={getIn(touched, `campaignContent[${index}].buttonName`) && getIn(errors, `campaignContent[${index}].buttonName`)}
                      helperText={getHelperText(`campaignContent[${index}].buttonName`)}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Container>
    </Dialog>
  );
}
